/**
 * Real-time multiplayer asteroids game with TypeScript/Bun server and HTML5 Canvas client
 * Client-side UI - Weapon loadout display panel
 * LoadoutPanel.tsx - Shows current weapon and provides button to open selection modal
 */

import { getWeaponDisplayName, type WeaponType } from "@game/config";
import type React from "react";
import { Panel } from "../ui/Panel";

// ============================================================================
// TYPES
// ============================================================================

/**
 * Props for LoadoutPanel component
 */
interface LoadoutPanelProps {
  /** Callback to open weapon selection modal */
  readonly onOpenModal: () => void;
  /** Currently selected primary weapon (controlled by container) */
  readonly currentWeapon: WeaponType;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * LoadoutPanel - Displays current weapon and button to change loadout
 *
 * **Purpose:**
 * - Shows player's currently equipped primary weapon
 * - Provides button to open weapon selection modal
 * - Loads weapon on mount from loadout service
 *
 * **Design:**
 * - Retro-terminal military aesthetic matching existing UI
 * - Uses Panel component for consistent styling
 * - Monospace uppercase text with green accents
 *
 * **State Management:**
 * - Loads current weapon on mount via loadoutService
 * - Updates when weapon changes (via external state update)
 *
 * @example
 * ```tsx
 * <LoadoutPanel onOpenModal={() => setModalOpen(true)} />
 * ```
 */
export const LoadoutPanel: React.FC<LoadoutPanelProps> = ({
  onOpenModal,
  currentWeapon,
}) => {
  return (
    <Panel title="LOADOUT" className="loadout-panel">
      <div className="loadout-content">
        <div className="current-weapon">
          <span className="loadout-label">PRIMARY:</span>
          <span className="loadout-weapon-name">
            {getWeaponDisplayName(currentWeapon)}
          </span>
        </div>

        <button
          type="button"
          className="loadout-change-button"
          onClick={onOpenModal}
        >
          CHANGE LOADOUT
        </button>
      </div>
    </Panel>
  );
};
